export interface APIConfig {
  apiBaseUrl: string;
  environment: 'dev' | 'prod';
  error?: string;
  isEnvironmentLocked?: boolean;
}

const DEFAULT_ENVIRONMENT: 'dev' | 'prod' = 'dev';

const rawBase = (import.meta.env.VITE_API_BASE_URL || '').trim();
const apiBaseUrl = rawBase.replace(/\/+$/, '');
const rawEnv = (import.meta.env.VITE_ENVIRONMENT || '').toString().trim().toLowerCase();
const environment: 'dev' | 'prod' =
  rawEnv.startsWith('prod') ? 'prod' :
  rawEnv.startsWith('dev') ? 'dev' : DEFAULT_ENVIRONMENT;
const error = !apiBaseUrl
  ? 'Missing TRAPI API base URL. Copy trapi-ui/.env.example to trapi-ui/.env or set VITE_API_BASE_URL before loading TMDS data.'
  : undefined;
const isEnvironmentLocked = !!(import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_ENVIRONMENT);

export const apiConfig: APIConfig = { apiBaseUrl, environment, error, isEnvironmentLocked };

// Task-suite endpoints require Front Door (APIM rejects direct calls with
// "Invalid Front Door request"). Use the public TRAPI URL for these.
const FRONT_DOOR_BASE: Record<string, string> = {
  dev: 'https://dev-trapi.research.microsoft.com/tmds',
  prod: 'https://trapi.research.microsoft.com/tmds',
};
const frontDoorUrl = (path: string) => {
  const base = FRONT_DOOR_BASE[environment] || FRONT_DOOR_BASE.dev;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

export const buildUrl = (path: string) => {
  if (error) {
    throw new Error(error);
  }

  return `${apiBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

export const endpoints = {
  myaccess: () => buildUrl('/myaccess'),
  models: () => buildUrl('/models'),
  examples: () => buildUrl('/examples'),
  managemsi: (apiId?: string) =>
    apiId ? buildUrl(`/managemsi/${encodeURIComponent(apiId)}`) : buildUrl('/managemsi'),
  idcheck: (id: string) => buildUrl(`/idcheck/${encodeURIComponent(id)}`),
  capacitycheck: (apiId: string, model: string) =>
    buildUrl(`/capacitycheck/${encodeURIComponent(apiId)}/${encodeURIComponent(model)}`),
  testconfig: () => buildUrl('/testconfig'),
  refresh: () => buildUrl('/refresh'),
  pathmappings: () => buildUrl('/pathmappings'),
  wikimodels: () => buildUrl('/wikimodels'),
  // Task-suite routes go through Front Door (APIM validates X-Azure-FDID header)
  taskSuiteLatest: (task: string) => frontDoorUrl(`/task-suite/latest/${encodeURIComponent(task)}?api-version=2026-03-01`),
  taskSuiteRun: () => frontDoorUrl('/task-suite/run?api-version=2026-03-01'),
  taskSuiteJobs: () => frontDoorUrl('/task-suite/jobs?api-version=2026-03-01'),
  taskSuiteJob: (id: string) => frontDoorUrl(`/task-suite/jobs/${encodeURIComponent(id)}?api-version=2026-03-01`),
};

export const defaultHeaders = (extra?: Record<string, string>) => ({
  'Content-Type': 'application/json',
  ...(extra || {}),
});

export type TokenProvider = () => Promise<string>;

/**
 * Shared authenticated-request header builder. Every service client
 * (tmds.ts, tokenusage.ts, ...) used to carry its own near-identical copy of
 * this function; centralizing it here avoids that duplication (see PR
 * #96968 thread 974825).
 */
export async function authHeaders(getToken: TokenProvider, extra?: Record<string, string>) {
  if (apiConfig.error) {
    throw new Error(apiConfig.error);
  }
  const token = await getToken();
  return {
    ...defaultHeaders(extra),
    Authorization: `Bearer ${token}`,
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function yearMonthQuery(year?: number, month?: number): string {
  if (year == null || month == null) return '';
  return `?year=${year}&month=${pad2(month)}`;
}

/**
 * Token Usage (PR 6/6) route builders, registered centrally like every
 * other `endpoints.*` entry above rather than as a separate local `paths`
 * object inside `tokenusage.ts` (see PR #96968 thread 974825).
 *
 * Confirmed final contract (cross-checked against PR4 commit 16001124 and
 * PR5 commit 57ec88ab's deployed bicep/policy XML):
 *   GET /mytokenusage                     - current/historical own usage (?year=&month=)
 *   GET /mytokenusage/periods             - available periods for own OID
 *   GET /mytokenusage/admin/{userOid}     - admin: specific user (?year=&month=)
 *   GET /mytokenusage/admin/org           - admin: org-wide aggregate (?year=&month=)
 * (The org-wide route was renamed from `/admin/all` to `/admin/org` by PR5
 * to fix an APIM route-ambiguity bug — "all" collided with the
 * `/admin/{userOid}` wildcard template.)
 */
export const tokenUsageEndpoints = {
  current: () => buildUrl('/mytokenusage'),
  historical: (year: number, month: number) => buildUrl(`/mytokenusage?year=${year}&month=${pad2(month)}`),
  periods: () => buildUrl('/mytokenusage/periods'),
  adminUser: (oid: string, year?: number, month?: number) =>
    buildUrl(`/mytokenusage/admin/${encodeURIComponent(oid)}${yearMonthQuery(year, month)}`),
  adminAll: (year?: number, month?: number) => buildUrl(`/mytokenusage/admin/org${yearMonthQuery(year, month)}`),
};
